import { useRouteContext } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { authClient } from "@/lib/auth";
import { classifySession, redirectToLogin, sessionErrorStatus, toUserOutput } from "@/lib/sessionAvailability";
import { useOptionalTaskList, useTaskLists } from "@/queries/list-queries";
import { useUsers } from "@/queries/queries";
import { useSuspenseTaskStatuses } from "@/queries/task-status-queries";
import { buildDraftTask } from "./buildDraftTask";
import { runAddTaskClick } from "./runAddTaskClick";
import { useTaskQueryParams } from "./TaskQueryParamsContext";

/**
 * Memoized because the returned builder feeds `handleAddTask`, which is the value
 * of AddTaskContext. Returning a fresh object per render made that context value
 * change on every table render, which re-rendered every title cell in the table
 * (a context change re-renders consumers regardless of their memo boundary).
 */
export function useTaskBuilder() {
    const { data: taskStatuses } = useSuspenseTaskStatuses();
    const { data: session, isPending, error } = authClient.useSession();
    // beforeLoad stored the user for this navigation. It stays put after the
    // client session expires, so it is only a fallback while that session is
    // still loading or the session request failed without saying unauthorized.
    const { user: routeUser } = useRouteContext({ from: "/app" });
    const queryParams = useTaskQueryParams();
    const currentTaskList = useOptionalTaskList(queryParams.taskListId);
    const { data: users } = useUsers({ enabled: true });
    const { data: taskLists } = useTaskLists();
    const { weekStartsOn } = useDateFormatter();

    const availability = classifySession({
        user: session?.user ?? null,
        isPending,
        hasError: error != null,
        errorStatus: sessionErrorStatus(error),
    });

    const build = useCallback(
        (title: string, groupColumnId: string | null, groupValue: any) => {
            return runAddTaskClick({
                availability,
                redirectToLogin,
                build: () => {
                    const currentUser =
                        toUserOutput(session?.user) ?? (availability === "signed-out" ? null : toUserOutput(routeUser));
                    return buildDraftTask({
                        title,
                        groupColumnId,
                        groupValue,
                        currentUser,
                        taskStatuses,
                        queryParams,
                        currentTaskList,
                        users,
                        taskLists,
                        weekStartsOn,
                    });
                },
            });
        },
        [availability, session, routeUser, taskStatuses, queryParams, currentTaskList, users, taskLists, weekStartsOn]
    );

    return useMemo(() => ({ build }), [build]);
}
